import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useSaveQcData } from '@/services/opu-search/hooks';
import type { QcDataResponse } from '@/services/opu-search/types';
import { STRING_MAP_SEARCH } from '@/types.gql';
import { formatDateWithPattern } from '@/utils';

import { QcDataSchema, qcDataSchema } from '../../utils/schema';
import { getTubeTypes } from '../../utils/utils';

import './OpuSearchQcInformationDialog.css';

type QCInormationDialogProps = {
  container?: React.ComponentProps<typeof Calendar>['container'];
  setShowQcInformation: (show: boolean) => void;
  qcData: QcDataResponse;
  refetchQcData: () => void;
};

export const OpuSearchQcInformationDialog = ({
  container,
  setShowQcInformation,
  qcData,
  refetchQcData,
}: QCInormationDialogProps) => {
  const { control, handleSubmit, reset, formState } = useForm<QcDataSchema>({
    defaultValues: {
      tog_qc_hepdate: undefined,
      tog_qc_matdate: undefined,
      tog_qc_opuholdingdate: undefined,
      tog_qc_opuholding: '',
      tog_qc_opuflushdate: undefined,
      tog_qc_opuflush: '',
      tog_qccomment: '',
      tog_qc_slide_warmer: '',
      tog_qc_tokai_hit: '',
      tog_qc_water_bath_temperature: '',
      tog_qcairqualityint: 0,
      tog_qc_cellstrainer: '',
      tog_qc_fiftymllotno: '',
      tog_qc_thirtymlsyringe: '',
      tog_qc_tenmlsyringe: '',
      tog_qc_fiftymltubetype: '0',
      tog_qc_rinsemedialotdate: undefined,
      tog_qc_incubatorno: '',
      qcFreezetemp: '',
    },
    resolver: zodResolver(qcDataSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
  });
  const onInvalid = (errors: typeof formState.errors) => {
    console.error('Form invalid:', errors);
  };
  const [params] = useSearchParams();
  const opuDate =
    params.get('date') || formatDateWithPattern(new Date(), 'MM-dd-yyyy');
  const opuSiteId = params.get('site') as string;
  const location = params.get('location') as string;

  const { mutateAsync: saveQcDataAsync, loading: updateQcDataLoading } =
    useSaveQcData();
  const airQualityData = useQuery(STRING_MAP_SEARCH, {
    variables: { objectTypeCode: 10006, name: 'tog_qcairquality' },
  })?.data?.stringMap;

  const tubeTypeOptions = getTubeTypes();

  useEffect(() => {
    if (qcData) {
      const formData = {
        tog_qc_hepdate: new Date(qcData.tog_qc_hepdate as string) || '',
        tog_qc_matdate: new Date(qcData.tog_qc_matdate as string) || '',
        tog_qc_opuholdingdate:
          new Date(qcData.tog_qc_opuholdingdate as string) || '',
        tog_qc_opuholding: qcData.tog_qc_opuholding || '',
        tog_qc_opuflushdate: qcData.tog_qc_opuflushdate
          ? new Date(qcData.tog_qc_opuflushdate as string)
          : undefined,
        tog_qc_opuflush: qcData.tog_qc_opuflush || '',
        tog_qccomment: qcData.tog_qccomment || '',
        tog_qc_slide_warmer: qcData.tog_qc_slide_warmer || '',
        tog_qc_tokai_hit: qcData.tog_qc_tokai_hit || '',
        tog_qc_water_bath_temperature:
          qcData.tog_qc_water_bath_temperature || '',
        tog_qcairqualityint: qcData.tog_qcairqualityint || undefined,
        tog_qc_cellstrainer: qcData.tog_qc_cellstrainer || '',
        tog_qc_fiftymllotno: qcData.tog_qc_fiftymllotno || '',
        tog_qc_thirtymlsyringe: qcData.tog_qc_thirtymlsyringe || '',
        tog_qc_tenmlsyringe: qcData.tog_qc_tenmlsyringe || '',
        tog_qc_fiftymltubetype: qcData.tog_qc_fiftymltubetype || '0',
        tog_qc_rinsemedialotdate:
          new Date(qcData.tog_qc_rinsemedialotdate as string) || '',
        tog_qc_incubatorno: qcData.tog_qc_incubatorno || '',
        qccompleted: qcData.qccompleted || false,
        qcFreezetemp: qcData.qcFreezetemp || '',
      };

      reset(formData);
    }
  }, [qcData, reset]);

  const onSubmit = async (data: QcDataSchema) => {
    const opuHoldingDate: Date | undefined =
      data.tog_qc_opuholdingdate ?? data.tog_qc_opuflushdate;
    const formData = {
      ...data,
      tog_qc_opuflushdate: formatDateWithPattern(
        data?.tog_qc_opuflushdate as Date,
        'MM/dd/yyyy',
      ),
      tog_qc_matdate: formatDateWithPattern(
        data?.tog_qc_matdate as Date,
        'MM/dd/yyyy',
      ),
      tog_qc_hepdate: formatDateWithPattern(
        data?.tog_qc_hepdate as Date,
        'MM/dd/yyyy',
      ),
      tog_qc_rinsemedialotdate: formatDateWithPattern(
        data?.tog_qc_rinsemedialotdate as Date,
        'MM/dd/yyyy',
      ),
      tog_qc_opuholdingdate: opuHoldingDate
        ? formatDateWithPattern(opuHoldingDate, 'MM/dd/yyyy')
        : undefined,
    };
    await saveQcDataAsync({
      input: formData,
      aspirationDate: opuDate,
      siteId: opuSiteId,
      location: location,
    }).then(() => {
      refetchQcData();
      setShowQcInformation(false);
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <div className="qc-information-dialog__header">
        <strong>
          QC Information must be entered prior to searching OPUs. All fields are
          required, except the comments.
        </strong>
      </div>
      <div className="opu-search-qc-information-dialog__fields">
        <div className="qc-information-dialog__section">
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_cellstrainer">Cell Strainer Lot #:</label>
            <Controller
              name="tog_qc_cellstrainer"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="tog_qc_cellstrainer"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_fiftymllotno">50 mL Tube Lot#:</label>
            <Controller
              name="tog_qc_fiftymllotno"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="tog_qc_fiftymllotno"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_fiftymltubetype">50 mL Tube Type:</label>
            <Controller
              name="tog_qc_fiftymltubetype"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <Dropdown
                    id="tog_qc_fiftymltubetype"
                    options={tubeTypeOptions}
                    optionLabel="label"
                    optionValue="value"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_thirtymlsyringe">30 mL Syringe Lot #</label>
            <Controller
              name="tog_qc_thirtymlsyringe"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="tog_qc_thirtymlsyringe"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_tenmlsyringe">10 mL Syringe Lot #</label>
            <Controller
              name="tog_qc_tenmlsyringe"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="tog_qc_tenmlsyringe"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_opuflush">Flush Lot #</label>
            <Controller
              name="tog_qc_opuflush"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="tog_qc_opuflush"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_opuflushdate">Flush Expiration</label>
            <Controller
              name="tog_qc_opuflushdate"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <Calendar
                    container={container}
                    id={field.name}
                    value={field.value ? new Date(field.value) : null}
                    showIcon
                    onChange={(e) => field.onChange(e.value)}
                    dateFormat="mm/dd/yy"
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_matdate">Mat Lot # (Creation Date)</label>
            <Controller
              name="tog_qc_matdate"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <Calendar
                    container={container}
                    id={field.name}
                    value={field.value ? new Date(field.value) : null}
                    showIcon
                    onChange={(e) => field.onChange(e.value)}
                    dateFormat="mm/dd/yy"
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
        </div>
        <div className="qc-information-dialog__section">
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_hepdate">
              Holding/Hep Lot # (Creation Date)
            </label>
            <Controller
              name="tog_qc_hepdate"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <Calendar
                    container={container}
                    id={field.name}
                    value={field.value ? new Date(field.value) : null}
                    showIcon
                    onChange={(e) => field.onChange(e.value)}
                    dateFormat="mm/dd/yy"
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_rinsemedialotdate">
              Rinse Media Lot # (Creation Date)
            </label>
            <Controller
              name="tog_qc_rinsemedialotdate"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <Calendar
                    container={container}
                    id={field.name}
                    value={field.value ? new Date(field.value) : null}
                    showIcon
                    onChange={(e) => field.onChange(e.value)}
                    dateFormat="mm/dd/yy"
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_incubatorno">Incubator #</label>
            <Controller
              name="tog_qc_incubatorno"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="tog_qc_incubatorno"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qcairqualityint"> Air Quality</label>
            <Controller
              name="tog_qcairqualityint"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <Dropdown
                    id="tog_qcairqualityint"
                    options={airQualityData || []}
                    optionLabel="value"
                    optionValue="key"
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qccomment">Comments</label>
            <Controller
              name="tog_qccomment"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="tog_qccomment"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_slide_warmer">Slide Warmer</label>
            <Controller
              name="tog_qc_slide_warmer"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="tog_qc_slide_warmer"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_tokai_hit">Tokai Hit</label>
            <Controller
              name="tog_qc_tokai_hit"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="tog_qc_tokai_hit"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="tog_qc_water_bath_temperature">
              Water Bath Temperature
            </label>
            <Controller
              name="tog_qc_water_bath_temperature"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="tog_qc_water_bath_temperature"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
          <div className="opu-search-qc-information-dialog__field">
            <label htmlFor="qcFreezetemp">Freezer Temperature</label>
            <Controller
              name="qcFreezetemp"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col">
                  <InputText
                    id="qcFreezetemp"
                    {...field}
                    className={fieldState.invalid ? 'p-invalid' : undefined}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && (
                    <small className="p-error">
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
        </div>
      </div>
      <div className="save-buttons">
        <Button
          icon="pi pi-times"
          label="Cancel"
          type="button"
          className="save"
          severity="secondary"
          onClick={() => setShowQcInformation(false)}
        />
        <Button
          icon="pi pi-check"
          label="Save"
          type="submit"
          className="save"
          loading={updateQcDataLoading}
        />
      </div>
    </form>
  );
};
